import { Box, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import AddButton from "components/common/AddButton";
import { useDiscussions } from "hooks/useDiscussions";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSocket } from "src/socket/useSocket";
import { useAuthStore } from "stores/authStore";
import { ChatRoomsHeader } from "./ChatRoomsHeader";
import { DiscussionListItem } from "./DiscussionListItem";
import { ModalNewPrivateDiscussion } from "./ModalNewPrivateDiscussion";
import { getContextDiscussionLabel } from "./chatRooms.utils";

interface ChatRoomsProps {
  onSelect: (discussionId: string, contextId?: string | null) => void;
  onClose?: () => void;
  isDesktop?: boolean;
}

const ChatRooms: React.FC<ChatRoomsProps> = ({
  onSelect,
  onClose,
  isDesktop,
}) => {
  const { t } = useTranslation("discussions");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let parent = rootRef.current?.parentElement;
    while (parent) {
      const { overflowY } = window.getComputedStyle(parent);
      if (overflowY === "auto" || overflowY === "scroll") {
        parent.scrollTop = 0;
        break;
      }
      parent = parent.parentElement;
    }
  }, []);

  const currentUserId = useAuthStore(state => state.currentUserId);
  const socket = useSocket();
  const [typingMessages, setTypingMessages] = useState<Record<string, string>>(
    {}
  );
  const [search, setSearch] = useState("");
  const [
    isNewDiscussionOpen,
    { open: openNewDiscussion, close: closeNewDiscussion },
  ] = useDisclosure(false);
  const {
    discussions,
    isPending: discussionsIsLoading,
    isSuccess,
  } = useDiscussions();

  useEffect(() => {
    if (!socket || !currentUserId) return;

    const handleTyping = ({
      discussionId: socketDiscussionId,
      typing,
      userId,
      userName,
    }: {
      discussionId: string;
      typing: boolean;
      userId: string;
      userName?: string;
    }) => {
      if (currentUserId === userId) return;
      setTypingMessages(prev => ({
        ...prev,
        [socketDiscussionId]: typing
          ? `${userName ?? ""} ${t("typing").toLowerCase()}`.trim()
          : "",
      }));
    };

    socket.on("typing", handleTyping);
    return () => {
      socket.off("typing", handleTyping);
    };
  }, [socket, currentUserId, t]);

  const filteredDiscussions = discussions.filter(d => {
    if (!search) return true;
    const label =
      d.discussionType === "private"
        ? d.participantName
        : getContextDiscussionLabel(d, t);
    return label?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Box ref={rootRef} className="bg-white">
      <ChatRoomsHeader
        search={search}
        onSearchChange={setSearch}
        isDesktop={isDesktop}
        onNewDiscussion={openNewDiscussion}
        onClose={onClose}
      />
      {discussionsIsLoading && <Text>Loading...</Text>}
      {isSuccess &&
        filteredDiscussions.map(discussion => (
          <DiscussionListItem
            key={discussion.id}
            discussion={discussion}
            currentUserId={currentUserId}
            typingMessage={
              discussion.id ? typingMessages[discussion.id] : undefined
            }
            onSelect={onSelect}
          />
        ))}
      {!isDesktop && (
        <AddButton onClick={openNewDiscussion} zIndex={350} />
      )}
      {isNewDiscussionOpen && (
        <ModalNewPrivateDiscussion
          onClose={closeNewDiscussion}
          onSelect={onSelect}
        />
      )}
    </Box>
  );
};

export default ChatRooms;
